// Shared property-placeholder substitution helpers.
//
// Used by:
//   - pages/CreateCampaignPage.js (post / flyer / postcard canvas elements)
//   - components/NewsletterBuilder.js (newsletter block content)
//
// Mirrors the backend's `_apply_property_to_canvas` mapping
// (/app/backend/routes/scraper.py) so a placeholder substituted server-side
// at draft creation looks identical to one substituted client-side on Edit.

// Build the substitution mapping from a property_data object. Returns
// `{ '{{address}}': '1234 Main St', '{{price}}': '1,250,000', ... }`.
// Keys with no value are still present (as empty string) — callers should
// skip empty values to preserve the raw token (so an empty `description`
// doesn't wipe out a designer-typed `{{description}}` placeholder).
export const buildPropertyMapping = (propertyData) => {
  if (!propertyData) return {};
  const priceValue = propertyData.price?.toString().replace(/[^0-9]/g, '') || '';
  const formattedPrice = priceValue ? `$${parseInt(priceValue).toLocaleString()}` : '';
  const priceWithoutDollar = priceValue ? parseInt(priceValue).toLocaleString() : '';
  const bedsValue = propertyData.beds || '';
  const bathsValue = propertyData.baths || '';
  const sqftValue = propertyData.sqft?.toString().replace(/[^0-9]/g, '') || '';
  const addressValue = propertyData.address || '';
  const summaryValue = propertyData.property_summary || propertyData.description || '';
  const keyFeatures = propertyData.key_features || [];
  const keyFeaturesText = keyFeatures.map(f => `✓ ${f}`).join('\n');

  // Derive street/city/state/zip. Prefer explicit fields; otherwise parse the
  // full address ("Street, City, ST ZIP" or "Street, City, ST, ZIP").
  let streetValue = propertyData.street || '';
  let cityValue = propertyData.city || '';
  let stateValue = String(propertyData.state || '');
  let zipValue = String(propertyData.zip || propertyData.zipcode || '');
  if ((!streetValue || !cityValue || !stateValue || !zipValue) && addressValue.includes(',')) {
    const parts = addressValue.split(',').map(p => p.trim()).filter(Boolean);
    if (parts.length) {
      if (!streetValue) streetValue = parts[0];
      if (parts.length >= 2 && !cityValue) cityValue = parts[1];
      if (parts.length >= 3) {
        const st = parts[2].split(/\s+/);
        if (st.length) {
          if (!stateValue) stateValue = st[0];
          if (st.length > 1 && !zipValue) zipValue = st[1];
        }
      }
      if (parts.length >= 4 && !zipValue) {
        zipValue = (parts[3].split(/\s+/)[0]) || parts[3];
      }
    }
  }
  if (!streetValue) streetValue = addressValue;
  const cityStateValue = [cityValue, stateValue].filter(Boolean).join(', ');

  return {
    '${{price}}': formattedPrice,
    '{{price}}': priceWithoutDollar,
    '{{address}}': addressValue,
    '{{street}}': streetValue,
    '{{state}}': stateValue,
    '{{zip}}': zipValue,
    '{{city_state}}': cityStateValue,
    '{{beds}}': bedsValue,
    '{{baths}}': bathsValue,
    '{{sqft}}': sqftValue,
    '{{description}}': summaryValue,
    '{{summary}}': summaryValue,
    '{{property_type}}': String(propertyData.property_type || ''),
    '{{year_built}}': String(propertyData.year_built || ''),
    '{{key_features}}': keyFeaturesText,
    '{{features}}': keyFeaturesText,
    '{{city}}': cityValue,
    '{{neighborhood}}': String(propertyData.neighborhood || cityValue || ''),
    '{{agent_name}}': String(propertyData.agent_name || ''),
    '{{agent_phone}}': String(propertyData.agent_phone || ''),
    '{{agent_email}}': String(propertyData.agent_email || ''),
    '{{agent_title}}': String(propertyData.agent_title || ''),
    '{{agent_license}}': String(propertyData.agent_license || ''),
    '{{agent_website}}': String(propertyData.agent_website || ''),
    '{{brokerage}}': String(propertyData.brokerage || ''),
  };
};

// Case-INSENSITIVE substitution on a single string. {{ADDRESS}}, {{Address}},
// {{address}} all match. Longer keys (e.g. `${{price}}`) processed first so
// they win over their shorter siblings.
export const substituteText = (text, propertyData) => {
  if (typeof text !== 'string' || !text.includes('{{')) return text;
  if (!propertyData) return text;
  const mapping = buildPropertyMapping(propertyData);
  const escapeRegex = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const keys = Object.keys(mapping).sort((a, b) => b.length - a.length);
  let out = text;
  for (const k of keys) {
    const val = mapping[k];
    if (!val) continue; // leave the raw token in place if no data
    const pattern = new RegExp(escapeRegex(k), 'gi');
    out = out.replace(pattern, val);
  }
  return out;
};
